import { Component, inject, Input } from '@angular/core';
import { AsyncPipe, NgClass } from '@angular/common';
import { Button } from 'primeng/button';
import { Droppable } from 'primeng/dragdrop';
import { Tooltip } from 'primeng/tooltip';
import {
  QuoteItemDataEditorService,
  QuoteItemDataPricingCadObject,
} from './quote-item-data-editor.service';
import { Blueprint } from '../../../../../models/blueprint.model';
import {
  PricingParameters,
  QuoteItemData,
} from '../../../../../models/quote-item-data.model';
import { PricingModel } from '../../../../../models/pricing-model.model';
import { QuoteItemDataTree } from './quote-item-data-tree/quote-item-data-tree.component';
import { QuoteItemLinkCadComponent } from './quote-item-link-cad/quote-item-link-cad.component';
import { QuoteItemLinkedCadModalComponent } from './quote-item-linked-cad-modal/quote-item-linked-cad-modal.component';
import { QuoteItemLinkedDrawingModalComponent } from './quote-item-linked-drawing-modal/quote-item-linked-drawing-modal.component';
import {
  ConfirmedDrawingRef,
  QuoteItemUploadDrawingComponent,
} from './quote-item-upload-drawing/quote-item-upload-drawing.component';
import {
  namedSuggestionsFromDrawingExtractions,
  resolveNamedBlueprintSuggestions,
} from '../../../../blueprints-viewer/blueprint-suggestions';
import { BlueprintSelectorModalComponent } from '../../../../blueprints-viewer/blueprint-selector-modal/blueprint-selector-modal.component';
import {
  CustomTreeProposalSummary,
  summarizeCustomQuoteTree,
} from '../../../../blueprints-viewer/custom-quote-tree';
import { customQuoteTreeHasDisplayableRoot } from '../../../../../models/drawing-file.model';
import { Equipment } from '../../../../../models/equipment.model';
import { Consumable } from '../../../../../models/consumable.model';
import { QuoteItemPricingParametersComponent } from './quote-item-pricing-parameters/quote-item-pricing-parameters.component';
import { MessageService } from 'primeng/api';
import { PricingModelService } from '../../../../services/pricing-model.service';

@Component({
  selector: 'app-quote-item-data-editor',
  imports: [
    AsyncPipe,
    NgClass,
    Button,
    Droppable,
    Tooltip,
    QuoteItemDataTree,
    QuoteItemLinkCadComponent,
    QuoteItemLinkedCadModalComponent,
    QuoteItemLinkedDrawingModalComponent,
    QuoteItemUploadDrawingComponent,
    BlueprintSelectorModalComponent,
    QuoteItemPricingParametersComponent,
  ],
  templateUrl: './quote-item-data-editor.component.html',
  styleUrl: './quote-item-data-editor.component.scss',
})
export class QuoteItemDataEditorComponent {
  quoteItemDataEditorService: QuoteItemDataEditorService = inject(
    QuoteItemDataEditorService,
  );
  pricingModelService = inject(PricingModelService);

  @Input() allBlueprints: Blueprint[] = [];
  @Input() equipmentOptions: Equipment[] = [];
  @Input() consumableOptions: Consumable[] = [];
  @Input() consumablesEnabled = false;
  @Input() tradeStudyToolFeaturesEnabled = false;
  @Input() pdfDrawingUploadEnabled = false;

  showBlueprintCadLink = false;
  showBulkCadUpload = false;
  showDrawingUpload = false;
  showBlueprintSelector = false;
  /** Confirmed drawings waiting for blueprint selection before entering the tree. */
  pendingDrawingFiles: ConfirmedDrawingRef[] = [];
  private drawingSuggestionsSource: ConfirmedDrawingRef[] | null = null;
  private cachedDrawingNameSuggestions = namedSuggestionsFromDrawingExtractions(
    [],
  );
  itemBeingCadLinked: QuoteItemData | null = null;
  itemBeingCadViewed: QuoteItemData | null = null;
  itemBeingDrawingViewed: QuoteItemData | null = null;
  itemBeingPricingEdited: QuoteItemData | null = null;
  pricingModelIdForEditedItem: number | null = null;
  cadObjectsForPricingEditedItem: QuoteItemDataPricingCadObject[] = [];

  private messageService = inject(MessageService);

  @Input() currencyCode?: string;

  get canUploadPDFDrawing(): boolean {
    return this.pdfDrawingUploadEnabled;
  }

  get rootItems(): QuoteItemData[] {
    const items = this.quoteItemDataEditorService.items();
    const rootItems = items.filter((item) => item.parent_id === null);
    return this.quoteItemDataEditorService.sortSiblingsBySiblingOrder(
      rootItems,
    );
  }

  onAddBlueprint(): void {
    this.showBlueprintCadLink = true;
  }

  onOpenBlueprintSelector(): void {
    this.showBlueprintSelector = true;
  }

  get hasPendingDrawings(): boolean {
    return this.pendingDrawingFiles.length > 0;
  }

  get blueprintSelectorHeader(): string {
    const drawing = this.pendingDrawingFiles[0];
    if (!drawing) {
      return 'Add Blueprint';
    }
    const remaining = this.pendingDrawingFiles.length;
    return remaining > 1
      ? `Choose structure for ${drawing.filename} (${remaining} remaining)`
      : `Choose structure for ${drawing.filename}`;
  }

  get drawingNameSuggestions() {
    if (this.drawingSuggestionsSource !== this.pendingDrawingFiles) {
      this.drawingSuggestionsSource = this.pendingDrawingFiles;
      this.cachedDrawingNameSuggestions =
        namedSuggestionsFromDrawingExtractions(this.pendingDrawingFiles);
    }
    return this.cachedDrawingNameSuggestions;
  }

  get drawingBlueprintConfirmLabel(): string {
    if (!this.hasPendingDrawings) {
      return 'Add to Quote';
    }
    return this.pendingDrawingFiles.length > 1
      ? 'Add drawings with blueprint'
      : 'Add drawing with blueprint';
  }

  get drawingSkipHint(): string {
    const count = this.pendingDrawingFiles.length;
    if (count <= 1) {
      return 'Or skip to add a single item without a blueprint';
    }
    return `Or skip to add ${count} items without a blueprint`;
  }

  get drawingSkipTooltip(): string {
    const count = this.pendingDrawingFiles.length;
    if (count <= 1) {
      return 'Add a single item with this drawing and no blueprint template';
    }
    return `Add ${count} items with these drawings and no blueprint template`;
  }

  /**
   * True when the first pending drawing's primary blueprint suggestion
   * resolves to a catalogue blueprint. Alternates and other drawings are
   * ignored so they can stay in the selector without hiding an AI custom tree draft.
   */
  get hasResolvableBlueprintSuggestion(): boolean {
    const drawing = this.pendingDrawingFiles[0];
    if (!drawing) {
      return false;
    }
    return (
      resolveNamedBlueprintSuggestions(
        this.allBlueprints,
        namedSuggestionsFromDrawingExtractions([
          {
            suggestions: { blueprint: drawing.suggestions?.blueprint },
          },
        ]),
      ).length > 0
    );
  }

  /**
   * AI draft for the first pending drawing. Shown alongside a catalogue
   * blueprint when both exist so the user can compare them.
   */
  get pendingCustomTreeProposal(): CustomTreeProposalSummary | null {
    if (!this.hasPendingDrawings) {
      return null;
    }
    const tree = this.pendingDrawingFiles[0]?.suggestions?.custom_tree;
    if (!customQuoteTreeHasDisplayableRoot(tree)) {
      return null;
    }
    return summarizeCustomQuoteTree(tree);
  }

  onBlueprintSelected(blueprint: Blueprint): void {
    if (this.pendingDrawingFiles.length) {
      this.quoteItemDataEditorService.addBlueprintWithDrawings(
        blueprint,
        this.allBlueprints,
        this.pendingDrawingFiles,
      );
      this.pendingDrawingFiles = [];
      return;
    }

    this.quoteItemDataEditorService.addBlueprintWithoutCad(
      blueprint,
      this.allBlueprints,
    );
  }

  onCustomTreeSelected(): void {
    const drawing = this.pendingDrawingFiles[0];
    const tree = drawing?.suggestions?.custom_tree;
    if (!drawing || !customQuoteTreeHasDisplayableRoot(tree) || !tree) {
      return;
    }
    this.quoteItemDataEditorService.addCustomTreeWithDrawing(
      tree,
      {
        id: drawing.id,
        filename: drawing.filename,
      },
      this.allBlueprints,
    );

    // Keep the rest of the batch and leave the selector open so the user
    // can pick a draft, catalogue blueprint, or skip for each remaining file.
    const remaining = this.pendingDrawingFiles.slice(1);
    this.pendingDrawingFiles = remaining;
    this.showBlueprintSelector = remaining.length > 0;
  }

  onBulkUpload(): void {
    this.showBulkCadUpload = true;
  }

  onUploadDrawing(): void {
    this.pendingDrawingFiles = [];
    this.showDrawingUpload = true;
  }

  drawingFileIdsInEditor(): number[] {
    return this.quoteItemDataEditorService
      .items()
      .flatMap((item) => (item.drawing_file_id ? [item.drawing_file_id] : []));
  }

  onDrawingConfirmed(drawings: ConfirmedDrawingRef[]): void {
    this.pendingDrawingFiles = drawings;
    this.showDrawingUpload = false;
    this.showBlueprintSelector = true;
  }

  onSkipDrawingBlueprint(): void {
    if (!this.pendingDrawingFiles.length) {
      return;
    }
    this.quoteItemDataEditorService.addDrawingsWithoutBlueprint(
      this.pendingDrawingFiles,
    );
    this.pendingDrawingFiles = [];
  }

  onConfirmBlueprintCadLink(items: QuoteItemData[]): void {
    this.quoteItemDataEditorService.addPreparedBlueprintItems(items);
    this.showBlueprintCadLink = false;
  }

  onAddBlankItem(): void {
    this.quoteItemDataEditorService.createBlankItem();
  }

  onOpenCadLinkForItem(item: QuoteItemData): void {
    if (item.drawing_file_id) {
      this.itemBeingDrawingViewed = item;
      return;
    }
    if (item.cad_object_ids.length === 0) {
      this.itemBeingCadLinked = item;
      return;
    }

    this.itemBeingCadViewed = item;
  }

  onRelinkCadForItem(item: QuoteItemData): void {
    this.itemBeingCadViewed = null;
    this.itemBeingCadLinked = item;
  }

  onConfirmCadLink(items: QuoteItemData[]): void {
    if (!this.itemBeingCadLinked || items.length === 0) {
      return;
    }

    const updatedItem = items.find(
      (item) => item.persistent_id === this.itemBeingCadLinked?.persistent_id,
    );

    if (!updatedItem) {
      return;
    }

    const patch: Partial<QuoteItemData> = {
      name: updatedItem.name,
      cad_object_ids: updatedItem.cad_object_ids,
      cad_object_names: updatedItem.cad_object_names,
    };

    this.quoteItemDataEditorService.updateItem(this.itemBeingCadLinked, patch);
  }

  onOpenPricingParametersForItem(item: QuoteItemData): void {
    let latestItem = this.getLatestItem(item);
    if (
      item.pricing_model_id &&
      item.pricing_model_id !== latestItem.pricing_model_id
    ) {
      this.quoteItemDataEditorService.updateItem(latestItem, {
        pricing_model_id: item.pricing_model_id,
      });
      latestItem = this.getLatestItem(item);
    }

    this.itemBeingPricingEdited = latestItem;
    this.pricingModelIdForEditedItem = item.pricing_model_id ?? null;
    this.cadObjectsForPricingEditedItem =
      this.quoteItemDataEditorService.getCadObjectsForPricing(latestItem);
  }

  onSavePricingParameters(params: PricingParameters | null): void {
    if (!this.itemBeingPricingEdited) {
      return;
    }

    const patch: Partial<QuoteItemData> = { pricing_parameters: params };
    if (this.pricingModelIdForEditedItem != null) {
      patch.pricing_model_id = this.pricingModelIdForEditedItem;
    }

    try {
      this.quoteItemDataEditorService.updateItem(
        this.itemBeingPricingEdited,
        patch,
      );

      this.messageService.add({
        severity: 'success',
        summary: 'Saved',
        detail: 'Pricing parameters were updated.',
      });

      this.onClosePricingParameters();
    } catch {
      this.messageService.add({
        severity: 'error',
        summary: 'Save failed',
        detail: 'Could not save pricing parameters.',
      });
    }
  }

  get pricingModelForEditedItem(): PricingModel | null {
    if (!this.itemBeingPricingEdited?.pricing_model_id) {
      return null;
    }

    return (
      this.pricingModelService.pricingModelsById()[
        this.itemBeingPricingEdited?.pricing_model_id
      ] ?? null
    );
  }

  onAddChildToItem(parentId: string): void {
    this.quoteItemDataEditorService.createBlankItem(undefined, parentId);
  }

  onRemoveItem(itemPersistentId: string): void {
    this.quoteItemDataEditorService.removeItem(itemPersistentId);
  }

  onClosePricingParameters(): void {
    this.itemBeingPricingEdited = null;
    this.pricingModelIdForEditedItem = null;
    this.cadObjectsForPricingEditedItem = [];
  }

  private getLatestItem(item: QuoteItemData): QuoteItemData {
    return (
      this.quoteItemDataEditorService
        .items()
        .find((candidate) => candidate.persistent_id === item.persistent_id) ??
      item
    );
  }
}
